// ─────────────────────────────────────────────────────────────────────────────
// FILE:    src/app/config/page-access.config.ts
// VERSION: v2.2.0 — Manage-Access auf DASHBOARD_VIEW abgesenkt (T8 — Req #7 Item 50, 2026-07-09)
// ZWECK:   Zentrale Definition welche Page welche Capability(s) braucht.
//          Wird vom projectAccessGuard ausgewertet.
//
// ÄNDERUNG v2.1.0 gegenüber v2.0.0:
// ────────────────────────────────────────────────────────────────────────────
//   "Drehscheiben"-Pages bekommen `requires: []` (leeres Array).
//   Der Guard winkt diese durch — keine Capability-Anforderung.
//
//   Begründung: Diese Pages sind die natürlichen Einstiegspunkte für
//   JEDEN authentifizierten User mit CAL. /project-select muss erreichbar
//   sein, sonst ist der User in der App "gefangen". Ähnlich /manage-account
//   und /my-profile — das sind Self-Service-Seiten.
//
//   Authentifizierung + CAL prüft bereits der calStatusGuard (Layer 1).
//   Hier wird also nichts ungeprüft — wir verzichten nur auf eine
//   ZUSÄTZLICHE Capability-Hürde, die für diese Pages keinen Sinn ergibt.
//
//   Betroffene Drehscheiben-Pages:
//     /project-select   — Hub für alle User
//     /manage-account   — eigenes Konto verwalten
//     /my-profile       — eigenes Profil
//     /rt-assessment    — eigener Riemann-Thomann Test
//     /project-create   — neues Projekt anlegen (jeder darf das in Phase 1)
//     /time             — UTC-Zeit-Tool
//     /project/:pslot/rt-assessment — RT im Projekt-Kontext
//
//   NICHT geändert (Capability-Check bleibt aktiv):
//     /app-management*  — echte Admin-Pages, Capability-Filter erwünscht
//     /thirdparty-config — Admin-Tool
//     Alle Project-CRUD-Pages (sprint/item/task/persona/...) — capability-getrieben
// ────────────────────────────────────────────────────────────────────────────
//
// HINWEIS zu Capabilities wie PROJECT_LIST_VIEW / OWN_PROFILE_VIEW:
//   Sie bleiben in capabilities.enum.ts definiert — werden aber HIER
//   nicht mehr als Guard-Anforderung gesetzt. Begründung: Konsistenz im
//   Design-Vokabular, Vorbereitung für Phase 2 (AI-Agent-Differenzierung,
//   feinere DB-Rollen). Heute kein Effekt im Guard.
// ─────────────────────────────────────────────────────────────────────────────

import { Capability } from '../core/auth/capabilities.enum';

export type PageAccessScope = 'project' | 'org' | 'public';

export interface PageAccessRule {
  pattern: string;
  scope: PageAccessScope;
  /**
   * Welche Capability(s) braucht die Page?
   * - leeres Array → "Drehscheibe": jeder authentifizierte User mit CAL darf
   * - eine oder mehrere Caps → ALLE müssen vorhanden sein (UND-Verknüpfung)
   */
  requires: Capability[];
  label: string;
  description: string;
}

export const PAGE_ACCESS_RULES: PageAccessRule[] = [

  // ═══════════════════════════════════════════════════════════════════════
  // PUBLIC ROUTES (gar nicht authentifiziert)
  // ═══════════════════════════════════════════════════════════════════════
  { pattern: '/login',                          scope: 'public', requires: [],
    label: 'Login', description: 'OAuth login' },
  { pattern: '/no-license',                     scope: 'public', requires: [],
    label: 'No License', description: 'Shown when user has no CAL' },
  { pattern: '/info-target-mapper',             scope: 'public', requires: [],
    label: 'Info Mapper', description: 'Redirect/info page' },
  { pattern: '/insufficient-permission',        scope: 'public', requires: [],
    label: 'Insufficient Permission', description: 'Shown when access denied' },
  { pattern: '/app-management/login',           scope: 'public', requires: [],
    label: 'App Management Login', description: 'Admin portal login' },
  { pattern: '/self-assign',                    scope: 'public', requires: [],
    label: 'Self-Assign CAL', description: 'Tryout-only self-assign' },

  // ═══════════════════════════════════════════════════════════════════════
  // DREHSCHEIBEN (authentifiziert + CAL, aber keine Capability-Hürde)
  // ═══════════════════════════════════════════════════════════════════════
  // Jede dieser Pages: scope='org' (kein pslot in der URL),
  // requires=[] (Guard winkt durch), calStatusGuard hat Session+CAL geprüft.
  // ═══════════════════════════════════════════════════════════════════════

  { pattern: '/project-select',                 scope: 'org', requires: [],
    label: 'Project Select',
    description: 'Choose a project to enter — the hub for all users' },

  { pattern: '/project-create',                 scope: 'org', requires: [],
    label: 'Create Project',
    description: 'Create a new project (Phase 1: any user can create)' },

  { pattern: '/manage-account',                 scope: 'org', requires: [],
    label: 'Manage Account',
    description: 'Personal account settings' },

  { pattern: '/my-profile',                     scope: 'org', requires: [],
    label: 'My Profile',
    description: 'Personal profile editor' },

  { pattern: '/rt-assessment',                  scope: 'org', requires: [],
    label: 'RT Assessment',
    description: 'Riemann-Thomann personality assessment (own)' },

  { pattern: '/time',                           scope: 'org', requires: [],
    label: 'Time View',
    description: 'UTC time tool' },

  // ═══════════════════════════════════════════════════════════════════════
  // ADMIN ORG-PAGES (Capability-Check aktiv — gewollt!)
  // ═══════════════════════════════════════════════════════════════════════

  { pattern: '/app-management/bulk-user-provisioning', scope: 'org',
    requires: [Capability.USER_PROVISION],
    label: 'Bulk User Provisioning',
    description: 'Pre-create users + assign CALs' },

  { pattern: '/app-management',                 scope: 'org',
    requires: [Capability.APP_MANAGEMENT_ACCESS],
    label: 'App Management',
    description: 'Admin portal' },

  { pattern: '/thirdparty-config',              scope: 'org',
    requires: [Capability.THIRD_PARTY_CONFIG],
    label: '3rd Party Config',
    description: 'Integration settings' },

  // ═══════════════════════════════════════════════════════════════════════
  // PROJECT-SCOPE: Views (jeder Projekt-Member, perm >= 1)
  // ═══════════════════════════════════════════════════════════════════════

  { pattern: '/project/:pslot/dashboard',       scope: 'project',
    requires: [Capability.DASHBOARD_VIEW],
    label: 'Dashboard', description: 'Project dashboard' },

  { pattern: '/project/:pslot/sprints/view',    scope: 'project',
    requires: [Capability.SPRINT_VIEW],
    label: 'Sprints View', description: 'View sprints' },
  { pattern: '/project/:pslot/sprints/view/date/:date', scope: 'project',
    requires: [Capability.SPRINT_VIEW],
    label: 'Sprint by Date', description: 'View sprint by date' },
  { pattern: '/project/:pslot/sprints/view/ceeq_spid/:ceeq_spid', scope: 'project',
    requires: [Capability.SPRINT_VIEW],
    label: 'Sprint by ID', description: 'View specific sprint' },
  // 2026-10-06 (PIA als HUGO, _40 #203 Task #694): SprintView — Lese-Seite eines Sprints
  { pattern: '/project/:pslot/sprint/:sp_id',   scope: 'project',
    requires: [Capability.SPRINT_VIEW],
    label: 'Sprint View', description: 'Read-only page of one sprint' },

  { pattern: '/project/:pslot/feature/dailyteamview/date/:date', scope: 'project',
    requires: [Capability.DAILY_VIEW],
    label: 'Daily Team View', description: 'Daily team view' },
  { pattern: '/project/:pslot/daily-scrum-matrix', scope: 'project',
    requires: [Capability.DAILY_VIEW],
    label: 'Daily Scrum Matrix', description: 'DSM overview' },
  { pattern: '/project/:pslot/daily-scrum-matrix/sprint/:sp_id', scope: 'project',
    requires: [Capability.DAILY_VIEW],
    label: 'DSM by Sprint', description: 'DSM for sprint' },
  { pattern: '/project/:pslot/daily-scrum-matrix/sprint/:sp_id/day/:cell_date/user/:uuid/edit',
    scope: 'project',
    requires: [Capability.DAILY_OWN_EDIT],
    label: 'DSM Day Edit', description: 'Edit own day' },

  { pattern: '/project/:pslot/items/view',      scope: 'project',
    requires: [Capability.ITEM_VIEW],
    label: 'Items View', description: 'View items' },
  { pattern: '/project/:pslot/item/:item_id',   scope: 'project',
    requires: [Capability.ITEM_VIEW],
    label: 'Item Detail', description: 'View item' },
  // 2026-10-27 (_40 #265): Meilenstein-Dashboard — liest Items, darum ITEM_VIEW (alle Projektrollen)
  { pattern: '/project/:pslot/milestone/:ms_id', scope: 'project',
    requires: [Capability.ITEM_VIEW],
    label: 'Milestone Dashboard', description: 'View milestone with its items' },

  // 2026-10-26 (_40 #266): Leseansicht eines Tasks — Lesefähigkeit TASK_SEEK (alle Projektrollen)
  { pattern: '/project/:pslot/task/:task_id',   scope: 'project',
    requires: [Capability.TASK_SEEK],
    label: 'Task Detail', description: 'View task' },
  { pattern: '/project/:pslot/tasks',           scope: 'project',
    requires: [Capability.TASK_SEEK],
    label: 'Task Seeker', description: 'Search tasks' },

  { pattern: '/project/:pslot/item-seeker',     scope: 'project',
    requires: [Capability.ITEM_SEEK],
    label: 'Item Seeker', description: 'Search items and features' },

  { pattern: '/project/:pslot/personas',        scope: 'project',
    requires: [Capability.PERSONA_VIEW],
    label: 'Personas', description: 'View personas' },
  { pattern: '/project/:pslot/persona/:persona_id', scope: 'project',
    requires: [Capability.PERSONA_VIEW],
    label: 'Persona Detail', description: 'View persona' },

  // RT Assessment im Project-Kontext: Drehscheibe-artig, jeder Member
  { pattern: '/project/:pslot/rt-assessment',   scope: 'project', requires: [],
    label: 'RT Assessment (Project)',
    description: 'Personal RT in project context' },

  // Pending User: hier hin um Zugang zu beantragen
  { pattern: '/project/:pslot/request-access',  scope: 'project', requires: [],
    label: 'Request Project Access',
    description: 'Pending user requests access' },

  // ═══════════════════════════════════════════════════════════════════════
  // PROJECT-SCOPE: Tasks/Items editieren
  // ═══════════════════════════════════════════════════════════════════════
  { pattern: '/project/:pslot/task/new/edit',   scope: 'project',
    requires: [Capability.TASK_CREATE],
    label: 'Task Creator', description: 'Create task' },
  { pattern: '/project/:pslot/task/:task_id/edit', scope: 'project',
    requires: [Capability.TASK_EDIT_OWN],
    label: 'Task Editor', description: 'Edit task' },
  { pattern: '/project/:pslot/item/:item_id/edit', scope: 'project',
    requires: [Capability.ITEM_EDIT_OWN],
    label: 'Item Editor', description: 'Edit item' },

  // ═══════════════════════════════════════════════════════════════════════
  // PROJECT-SCOPE: Manager-Aufgaben
  // ═══════════════════════════════════════════════════════════════════════
  { pattern: '/project/:pslot/sprint/new/edit', scope: 'project',
    requires: [Capability.SPRINT_CREATE],
    label: 'Sprint Creator', description: 'Create sprint' },
  { pattern: '/project/:pslot/sprint/:sp_id/edit', scope: 'project',
    requires: [Capability.SPRINT_EDIT],
    label: 'Sprint Editor', description: 'Edit sprint' },
  { pattern: '/project/:pslot/sprintsettingsset', scope: 'project',
    requires: [Capability.SPRINT_CREATE],
    label: 'Sprint Settings (Legacy)', description: 'Legacy sprint creator' },

  { pattern: '/project/:pslot/item/new/edit',   scope: 'project',
    requires: [Capability.ITEM_CREATE],
    label: 'Item Creator', description: 'Create item' },

  { pattern: '/project/:pslot/milestone/new/edit', scope: 'project',
    requires: [Capability.MILESTONE_CREATE],
    label: 'Milestone Creator', description: 'Create milestone' },
  { pattern: '/project/:pslot/milestone/:ms_id/edit', scope: 'project',
    requires: [Capability.MILESTONE_EDIT],
    label: 'Milestone Editor', description: 'Edit milestone' },
  { pattern: '/project/:pslot/milestones/create', scope: 'project',
    requires: [Capability.MILESTONE_CREATE],
    label: 'Milestones Creator (Legacy)', description: 'Legacy milestone creator' },

  { pattern: '/project/:pslot/showroom/task-tree', scope: 'project',
    requires: [Capability.WBS_VIEW],
    label: 'Showroom Task Tree', description: 'Sample data: tasks of an item as PSP tree (2026-10-28)' },

  { pattern: '/project/:pslot/wbs', scope: 'project',
    requires: [Capability.WBS_VIEW],
    label: 'WBS-Maker', description: 'WBS/PSP tree — view, create, and edit nodes' },

  { pattern: '/project/:pslot/persona/new/edit', scope: 'project',
    requires: [Capability.PERSONA_MANAGE],
    label: 'Persona Maker', description: 'Create persona' },
  { pattern: '/project/:pslot/persona/:persona_id/edit', scope: 'project',
    requires: [Capability.PERSONA_MANAGE],
    label: 'Persona Editor', description: 'Edit persona' },

  { pattern: '/project/:pslot/solution/new/edit', scope: 'project',
    requires: [Capability.SOLUTION_CREATE],
    label: 'Solution Creator', description: 'Create solution' },
  { pattern: '/project/:pslot/solution/:solution_id/edit', scope: 'project',
    requires: [Capability.SOLUTION_EDIT],
    label: 'Solution Editor', description: 'Edit solution' },

  // ═══════════════════════════════════════════════════════════════════════
  // PROJECT-SCOPE: Project Admin
  // ═══════════════════════════════════════════════════════════════════════
  // T8 (Req #7 Item 50) — Absenkung von PROJECT_ACCESS_MANAGE auf DASHBOARD_VIEW
  // Begründung: Manage-Access enthält ab T9 eine Prefs-Sektion (Kategorie + Prio)
  // die JEDER Member (perm >= 1, project_viewer+) für sich selbst nutzen darf.
  // Admin-Aktionen (Rollen vergeben, User sperren) bleiben im Template via
  // *hasCapability='PROJECT_ACCESS_MANAGE' geschützt — kein Security-Verlust.
  { pattern: '/project/:pslot/manage-access',   scope: 'project',
    requires: [Capability.DASHBOARD_VIEW],       // VIEWER+: perm >= 1 (war: PROJECT_ACCESS_MANAGE)
    label: 'Manage Access', description: 'Manage project members + own preferences' },
];

// ── Pattern matching (unverändert) ──────────────────────────────────────────
function patternToRegex(pattern: string): RegExp {
  const escaped = pattern
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/:[a-zA-Z_][a-zA-Z0-9_]*/g, '([^/]+)')
    .replace(/\*/g, '.*');
  return new RegExp('^' + escaped + '/?$');
}

const regexCache = new Map<string, RegExp>();
function getRegex(pattern: string): RegExp {
  let r = regexCache.get(pattern);
  if (!r) { r = patternToRegex(pattern); regexCache.set(pattern, r); }
  return r;
}

export function findPageAccessRule(url: string): PageAccessRule | null {
  const path = url.split('?')[0].split('#')[0];
  for (const rule of PAGE_ACCESS_RULES) {
    if (getRegex(rule.pattern).test(path)) return rule;
  }
  return null;
}
